/**
 * (owner, Sep 30: "attachments for guns: scopes and laser sights (figure out a nice way that these would ease aiming), suppressors, grips
 * and a weapon app to configure the weapons and shuffle their positions") An attachment is an item (content `attach`) mounted on a
 * weapon in the Weapons app — one per slot (optic, muzzle, laser, grip) on a class it fits; it leaves the pockets while mounted and
 * comes back when taken off. What it does:
 *  - the spread: a red dot ×0.8, a scope ×0.45 standing still, a laser ×0.7 always, a grip ×0.65 on the move (`effective`);
 *  - a suppressor: the shot heard and feared from a third as far, no muzzle flash, 8 % less punch;
 *  - what you see: a laser's red beam along the aim (everyone), a red dot at the cursor, a scope's view leaning toward the cursor
 *    (engine; main.ts), a grip halves the kick drawn.
 */
import type { AttachSlot, Item, Weapon } from '../content/types';
import type { Registry } from '../content/registry';
import type { Person, WeaponSlot } from './entities';
import type { Game, PlayerState } from './game';

export const ATTACH_SLOTS: AttachSlot[] = ['optic', 'muzzle', 'laser', 'grip'];
export const FIREARMS = new Set(['pistol', 'smg', 'rifle', 'shotgun', 'sniper', 'lmg']);

export function modItems(reg: Registry, w: WeaponSlot | undefined): Item[] { return w?.mods ? Object.values(w.mods).map((id) => (id ? reg.get('item', id) : undefined)).filter((it): it is Item => !!it?.attach) : []; }
export function fits(it: Item, def: Weapon): boolean { return !!it.attach && FIREARMS.has(def.class) && (!it.attach.fits || it.attach.fits.includes(def.class as never)); }
/** the weapon as fired with its attachments: its spread (still or moving) and its damage */
export function effective(reg: Registry, w: WeaponSlot, moving: boolean): Weapon {
  const mods = modItems(reg, w); if (!mods.length) return w.def;
  let spread = w.def.spread, damage = w.def.damage;
  for (const it of mods) { const a = it.attach!; if (a.spread && (!a.still || !moving)) spread *= a.spread; if (a.moveSpread && moving) spread *= a.moveSpread; if (a.damage) damage *= a.damage; }
  return { ...w.def, spread, damage, ...(mods.some((it) => it.attach!.quiet) ? { quiet: true } : {}) };
}
export function hasMod(reg: Registry, w: WeaponSlot | undefined, what: 'quiet' | 'laser' | 'dot'): boolean { return modItems(reg, w).some((it) => !!it.attach?.[what]); }
export function leadOf(reg: Registry, w: WeaponSlot | undefined): number { return Math.max(0, ...modItems(reg, w).map((it) => it.attach?.lead ?? 0)); }
export function kickOf(reg: Registry, w: WeaponSlot | undefined): number { return modItems(reg, w).reduce((k, it) => k * (it.attach?.kick ?? 1), 1); }

/** the Weapons app: mount an item from the pockets on weapon `i` (what was in its slot comes back to the pockets) */
export function mount(game: Game, st: PlayerState, i: number, itemId: string): void {
  const p = st.person, w = p.weapons[i], it = game.reg.get('item', itemId);
  if (!w || !it?.attach || (p.items[itemId] ?? 0) <= 0) return;
  if (!fits(it, w.def)) { game.say(`${it.name} does not fit the ${w.def.name}`, p); return; }
  const slot = it.attach.slot, old = w.mods?.[slot];
  if (old) p.items[old] = (p.items[old] ?? 0) + 1;
  p.items[itemId]--; if (p.items[itemId] <= 0) delete p.items[itemId];
  (w.mods ??= {})[slot] = itemId;
  game.say(`🔧 ${it.name} on the ${w.def.name}`, p); game.events.push({ kind: 'gear', x: p.x, y: p.y, cls: 'attach' });
}
export function unmount(game: Game, st: PlayerState, i: number, slot: AttachSlot): void {
  const p = st.person, w = p.weapons[i], id = w?.mods?.[slot]; if (!w || !id) return;
  delete w.mods![slot]; if (!Object.keys(w.mods!).length) delete w.mods;
  p.items[id] = (p.items[id] ?? 0) + 1;
  game.say(`🔧 ${game.reg.get('item', id)?.name ?? id} off the ${w.def.name}`, p); game.events.push({ kind: 'gear', x: p.x, y: p.y, cls: 'attach' });
}
/** move weapon `i` one place up (−1) or down (+1) in the order — the one in the hand stays in the hand */
export function move(st: PlayerState, i: number, dir: -1 | 1): void {
  const p = st.person, j = i + dir; if (i < 0 || j < 0 || i >= p.weapons.length || j >= p.weapons.length) return;
  const held = p.weapons[p.weaponIndex];
  [p.weapons[i], p.weapons[j]] = [p.weapons[j], p.weapons[i]];
  p.weaponIndex = Math.max(0, p.weapons.indexOf(held));
}
/** the one command the app sends */
export function armsCmd(game: Game, st: PlayerState, op: 'mount' | 'unmount' | 'move' | 'drop', i: number, arg: string | number): void {
  if (op === 'drop') { game.dropWeapon(st, i, typeof arg === 'string' ? arg : undefined); return; } // (owner, Oct 1: the Weapons app drops one too)
  if (op === 'mount' && typeof arg === 'string') mount(game, st, i, arg);
  else if (op === 'unmount' && typeof arg === 'string' && (ATTACH_SLOTS as string[]).includes(arg)) unmount(game, st, i, arg as AttachSlot);
  else if (op === 'move' && (arg === 1 || arg === -1)) move(st, i, arg);
}
/** a person's weapon in hand has a laser on (for the renderer and the wire) */
export function laserOn(reg: Registry, p: Person): boolean { return !p.vehicle && !p.dead && hasMod(reg, p.weapons[p.weaponIndex], 'laser'); }
